import { useEffect, useState } from "react";
import { Loader, Switch, ToastService } from "@launchmetrics/lm-ui";
import BssoPermissions from "components/LM/Bsso/Permissions";
import ModalDisplay from "components/LM/common/ModalDisplay";
import K2Api from 'lib/ajax/K2Api';
import './InstagramOfficialCrawlingSwitch.scss';

const K2_API = new K2Api();

function InstagramOfficialCrawlingSwitch({ voiceId, profileId }) {
	const [enabled, setEnabled] = useState(false);
	const [loading, setLoading] = useState(true);
	const [showModal, setShowModal] = useState(false);

	const url = `/voices/${voiceId}/socialmedia/instagram/${profileId}/fbgraph`;

	const enableText = "You are about to change this instagram profile to be crawled by the official API. Normally this action is done because the current instagram profile is age restricted. To verify this, please try to navigate to the profile without being logged in to Instagram.";
	const disableText = "You are about to disable this instagram profile from being crawled by the official Instagram API. The profile will revert to the default scraper crawling method.";

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		K2_API.get(url)
			.then(response => {
				if (!cancelled) {
					setEnabled(Boolean(response.data?.active));
				}
			})
			.catch(() => {
				if (!cancelled) {
					ToastService.show({
						id: "read-official-crawling-failed",
						title: "Failed to read the official API crawling state",
						type: "error",
					});
				}
			})
			.finally(() => {
				if (!cancelled) {
					setLoading(false);
				}
			});
		return () => {
			cancelled = true;
		};
	}, [url]);

	const handleToggleClick = () => {
		setShowModal(true);
	};

	const handleConfirm = async () => {
		setShowModal(false);
		setLoading(true);
		try {
			const httpMethod = !enabled ? "put" : "delete";
			await K2_API[httpMethod](url);
			setEnabled(!enabled);
		} catch (error) {
			ToastService.show({
				id: "toggle-official-crawling-failed",
				title: "Failed to toggle the official API crawling",
				type: "error",
			});
		} finally {
			setLoading(false);
		}
	};

	const handleCancel = () => {
		setShowModal(false);
	};

	return (
		<BssoPermissions permissions={['voices', 'edit']}>
			<div className="mt-3 official-crawling-switch">
				<Switch
					label="Official Instagram API crawling"
					checked={enabled}
					disabled={loading}
					onChange={handleToggleClick}
				/>
				{loading && <Loader size={16} />}
			</div>
			<ModalDisplay
				isVisible={showModal}
				headerText="Official API Crawling"
				contentText={enabled ? disableText : enableText}
				confirmButtonText={enabled ? "Disable" : "Enable"}
				onCancel={handleCancel}
				onConfirm={handleConfirm}
			/>
		</BssoPermissions>
	);
}

export default InstagramOfficialCrawlingSwitch;
